import React, { useEffect } from 'react';
import './App.css';
import './Privacy.css';

const CONTACT_EMAIL = 'walkitoffhealth@gmail.com';
const LAST_UPDATED = 'October 20, 2026';

function Terms() {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    document.title = 'Terms of Service | Walk It Off';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App">
      <nav className="nav">
        <a href="/" className="logo-container" aria-label="Walk It Off home">
          <img
            src="/walkitofflogo-dark.svg"
            alt="Walk It Off"
            className="logo-horizontal"
            width="350"
            height="130"
          />
        </a>
        <a href="https://apps.apple.com/us/app/walk-it-off-daily-walk-habit/id6818257483" className="nav-cta privacy-nav-cta" target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </nav>

      <main className="privacy-page">
        <div className="privacy-container">
          <h1 className="privacy-title">Terms of Service</h1>
          <p className="privacy-updated">Last updated: {LAST_UPDATED}</p>

          <p>
            These terms govern your use of the Walk It Off mobile app (iOS and Android), our website
            walkitoff.xyz, and our waitlist (together, the "service"), operated by Abimbola Labs LLC
            ("Walk It Off", "we", "us"). By creating an account or using
            the service, you agree to these terms and to our <a href="/privacy">Privacy Policy</a>.
            If you do not agree, please do not use Walk It Off.
          </p>

          <h2>Eligibility</h2>
          <p>
            You must be at least 18 years old to use Walk It Off. By creating an account, you
            confirm that you are 18 or older and able to agree to these terms.
          </p>

          <h2>Your account</h2>
          <p>
            You sign in with Google or Sign in with Apple. You are responsible for activity on your
            account and for keeping your sign-in credentials secure. Give us accurate information, and
            email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you think your
            account has been misused. You can delete your account at any time from within the app.
            Our{' '}
            <a href="/privacy">Privacy Policy</a> explains what we collect and how we use it.
          </p>

          <h2>How Walk It Off works</h2>
          <p>
            Walk It Off helps you build a daily walking habit. You set a daily step goal, and the app
            reads your step count from Apple Health or Health Connect, with your permission, to track
            your progress and streak.
          </p>
          <p>Putting credits on your goal is optional. You can:</p>
          <ul>
            <li>just track your steps in Practice Mode, with no credits involved; or</li>
            <li>put virtual credits on your daily goal for extra motivation.</li>
          </ul>
          <p>
            Each day is settled the following morning. If you reached your goal, your streak grows
            and any credits you put on that day stay with you. If you did not, your streak resets
            and any credits you put on that day go to the community pool. In Practice Mode, no
            credits move.
          </p>
          <p>
            You can change your goal, or switch between tracking only and using credits, at any
            time. Changes apply from the next day.
          </p>

          <h2 id="health-disclaimer">Health disclaimer</h2>
          <p>
            Walk It Off is a motivation tool, not a medical service. Nothing in the app or on our
            website is medical advice, and it is not a substitute for advice from a doctor or other
            qualified health professional. Talk to your doctor before starting or increasing a
            walking or exercise program, especially if you are pregnant, have a heart, lung, joint,
            or other medical condition, or take medication.
          </p>
          <p>
            Choose goals that are safe for you. Stop and seek medical help if you feel pain,
            dizziness, shortness of breath, or chest discomfort. Pay attention to your surroundings
            when you walk. You walk and exercise at your own risk.
          </p>

          <h2>Step data and settlement</h2>
          <p>
            Your daily goals are settled using step counts from Apple Health or Health Connect. We
            rely on the data your device and health app report, and we cannot guarantee that it is
            complete or accurate. We settle each day using the highest step total your app synced
            for that day. If your device fails to record or sync your steps, we may not be able to
            credit them.
          </p>
          <p>
            If you believe a day was settled incorrectly, use the dispute option on that day in the
            app. We review disputes in good faith, and our decision is final.
          </p>

          <h2>Credits</h2>
          <p>
            Walk It Off uses virtual credits only. Credits have no cash value. No payments are
            processed and nothing is paid out. Credits cannot be bought, sold, transferred,
            exchanged, or redeemed for money, goods, services, or prizes.
          </p>
          <p>
            Credit balances, the community pool, and challenge totals exist only inside the
            service. We may adjust them to correct errors or abuse, and we may reset or retire
            credits as we develop the service, for example if we introduce real-money features.
          </p>
          <p>
            If we later offer features involving real money, separate terms will explain how they
            work. We will ask you to accept those terms before you use those features.
          </p>

          <h2>Challenges</h2>
          <p>
            Challenges, including the Founding Members Challenge, have the start date, length, and
            rules shown in the app. You can join a challenge whether or not you put credits on your
            goal. Challenge pool totals are made up of virtual credits and have no
            cash value.
          </p>

          <h2>Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>
              Fake, simulate, or manipulate step counts, for example with devices, apps, or other
              people carrying your phone.
            </li>
            <li>
              Create more than one account (for example, to gain an advantage in challenges) or use
              someone else's account.
            </li>
            <li>
              Interfere with, reverse engineer, or attack the service or its users, or access it by
              automated means.
            </li>
            <li>Use the service for anything unlawful.</li>
          </ul>
          <p>
            We may suspend or close accounts that break these terms, and void credits or results
            earned by breaking them.
          </p>

          <h2>Changes to the service</h2>
          <p>
            Walk It Off is in active development. We may add, change, or remove features, or stop
            offering the service, at any time.
          </p>

          <h2>Our content</h2>
          <p>
            The Walk It Off name, logo, app, and website content belong to us. We give you a
            personal, non-transferable right to use the app for its intended purpose while these
            terms are in effect.
          </p>

          <h2>Disclaimers and limitation of liability</h2>
          <p>
            The service is provided "as is" and "as available", without warranties of any kind, to
            the fullest extent permitted by law, including any warranty that the service will be
            accurate, uninterrupted, or error-free. To the fullest extent permitted by law, Walk It Off
            is not liable for any indirect, incidental, or consequential damages, or for any injury
            or health problem arising from your walking or exercise. Our total liability for any
            claim relating to the service is limited to USD 50. Some places do not allow these
            limits, so they may not apply to you. Nothing in these terms limits
            rights you have under laws that cannot be waived.
          </p>

          <h2>Governing law</h2>
          <p>
            These terms are governed by the laws of the State of Washington, United States, without
            regard to its conflict-of-law rules. Any dispute arising from these terms or the service
            will be brought exclusively in the state or federal courts located in the State of
            Washington, and you and Abimbola Labs LLC consent to their jurisdiction.
          </p>

          <h2>Ending your use</h2>
          <p>
            You can stop using Walk It Off and delete your account at any time. We may suspend or
            end your access if you break these terms or if we stop offering the service.
          </p>

          <h2>Changes to these terms</h2>
          <p>
            We may update these terms from time to time. When we do, we will change the "Last
            updated" date above and, for significant changes, notify you in the app or by email. If
            you keep using Walk It Off after the changes take effect, you accept the updated terms.
          </p>

          <h2>Contact us</h2>
          <p>
            Questions about these terms? Email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>

          <a href="/" className="privacy-back">← Back to Walk It Off</a>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-bottom">
          <span>walkitoff.xyz</span>
          <span>© {currentYear} Walk It Off. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default Terms;
